import { memo, useEffect, useState, useRef, useMemo } from 'react';
import {
  X,
  FolderOpen,
  Plus,
  Trash2,
  Copy,
  Layers,
  Search,
  Loader2,
  FolderPlus,
  Zap,
  Pencil,
  Check,
  CheckSquare,
  Square,
  AlertTriangle,
  Star,
} from 'lucide-react';
import { useWorkflowStore } from '../../store/workflowStore';
import { toast } from 'sonner';
import * as projectService from '../../services/projectService';

const ProjectsPanel = memo(({ isOpen, onClose }) => {
  const savedProjects = useWorkflowStore(s => s.savedProjects);
  const loadSavedProjects = useWorkflowStore(s => s.loadSavedProjects);
  const loadProject = useWorkflowStore(s => s.loadProject);
  const deleteProject = useWorkflowStore(s => s.deleteProject);
  const duplicateProject = useWorkflowStore(s => s.duplicateProject);
  const saveProjectAsNew = useWorkflowStore(s => s.saveProjectAsNew);
  const currentProjectId = useWorkflowStore(s => s.currentProjectId);
  const isLoadingProjects = useWorkflowStore(s => s.isLoadingProjects);
  const hasUnsavedChanges = useWorkflowStore(s => s.hasUnsavedChanges);
  const saveProject = useWorkflowStore(s => s.saveProject);
  const setProjectName = useWorkflowStore(s => s.setProjectName);
  const newProject = useWorkflowStore(s => s.newProject);

  const [searchQuery, setSearchQuery] = useState('');
  const [loadingId, setLoadingId] = useState(null);

  // Selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteMode, setBulkDeleteMode] = useState(null); // 'selected' | 'all' | null
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Create new project state
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newName, setNewName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const createInputRef = useRef(null);

  // Rename state
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [pendingLoadId, setPendingLoadId] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const renameInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadSavedProjects();
      setSearchQuery('');
      setShowCreateInput(false);
      setRenamingId(null);
      setSelectedIds(new Set());
      setBulkDeleteMode(null);
    }
  }, [isOpen, loadSavedProjects]);

  useEffect(() => {
    if (showCreateInput && createInputRef.current) createInputRef.current.focus();
  }, [showCreateInput]);

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return savedProjects;
    return savedProjects.filter(p => p.name.toLowerCase().includes(q));
  }, [savedProjects, searchQuery]);

  if (!isOpen) return null;

  const selectedCount = selectedIds.size;
  const allVisibleSelected = filtered.length > 0 && filtered.every(p => selectedIds.has(p.id));

  const toggleSelect = (id, e) => {
    e?.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filtered.forEach(p => next.delete(p.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filtered.forEach(p => next.add(p.id));
        return next;
      });
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleLoad = async (projectId) => {
    if (renamingId) return;
    if (projectId === currentProjectId) { onClose(); return; }
    // Con cambios sin guardar: Guardar / Descartar / Cancelar (antes un
    // confirm de dos opciones perdía lo del usuario en silencio).
    if (hasUnsavedChanges) { setPendingLoadId(projectId); return; }
    await doLoad(projectId);
  };

  const doLoad = async (projectId) => {
    setLoadingId(projectId);
    const ok = await loadProject(projectId);
    setLoadingId(null);
    if (ok) {
      toast.success('Proyecto cargado', { duration: 1500, position: 'bottom-right' });
      onClose();
    }
  };

  // ── Create with name ───────────────────────────
  const handleShowCreate = () => { setShowCreateInput(true); setNewName(''); };

  const handleCreateConfirm = async () => {
    const name = newName.trim();
    if (!name) { toast.error('El nombre es obligatorio', { position: 'bottom-right' }); return; }
    setIsCreating(true);
    try {
      if (hasUnsavedChanges) {
        try { await saveProject(); } catch (e) { console.warn('Auto-save failed:', e.message); }
      }
      await saveProjectAsNew(name, '');
      setShowCreateInput(false);
      setNewName('');
      toast.success(`"${name}" creado`, { duration: 1500, position: 'bottom-right' });
      onClose();
    } catch (err) {
      toast.error('Error al crear proyecto', { position: 'bottom-right' });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateKeyDown = (e) => {
    if (e.key === 'Enter') handleCreateConfirm();
    if (e.key === 'Escape') setShowCreateInput(false);
  };

  // ── Rename ─────────────────────────────────────
  const startRename = (project, e) => {
    e.stopPropagation();
    setRenamingId(project.id);
    setRenameValue(project.name);
  };

  const handleRenameConfirm = async (projectId) => {
    const name = renameValue.trim();
    if (!name) { setRenamingId(null); return; }
    setIsRenaming(true);
    try {
      await projectService.renameProject(projectId, name);
      if (projectId === currentProjectId) setProjectName(name);
      await loadSavedProjects();
      toast.success(`Renombrado a "${name}"`, { duration: 1500, position: 'bottom-right' });
    } catch {
      toast.error('Error al renombrar', { position: 'bottom-right' });
    } finally {
      setRenamingId(null);
      setIsRenaming(false);
    }
  };

  const handleRenameKeyDown = (e, projectId) => {
    if (e.key === 'Enter') handleRenameConfirm(projectId);
    if (e.key === 'Escape') setRenamingId(null);
  };

  // ── Other actions ──────────────────────────────
  const handleDuplicate = async (projectId, e) => {
    e.stopPropagation();
    try {
      await duplicateProject(projectId);
      toast.success('Proyecto duplicado', { duration: 1500, position: 'bottom-right' });
    } catch (err) { console.error('Duplicate failed:', err.message); }
  };

  const handleSingleDelete = async (projectId, e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este proyecto? Esta acción no se puede deshacer.')) return;
    try {
      await deleteProject(projectId);
      setSelectedIds(prev => {
        const n = new Set(prev); n.delete(projectId); return n;
      });
      toast.success('Proyecto eliminado', { duration: 1500, position: 'bottom-right' });
    } catch (err) {
      toast.error('Error al eliminar', { position: 'bottom-right' });
    }
  };

  const handleToggleTemplate = async (project, e) => {
    e.stopPropagation();
    try {
      const res = await projectService.toggleTemplate(project.id);
      await loadSavedProjects();
      toast.success(
        res?.is_template ? `"${project.name}" ahora es plantilla` : `"${project.name}" ya no es plantilla`,
        { duration: 1800, position: 'bottom-right' }
      );
    } catch (err) {
      toast.error('Error al cambiar plantilla', { position: 'bottom-right' });
    }
  };

  // ── Bulk delete ────────────────────────────────
  const handleConfirmBulkDelete = async () => {
    const mode = bulkDeleteMode;
    if (!mode) return;
    setIsBulkDeleting(true);
    try {
      const payload = mode === 'all'
        ? { all: true }
        : { projectIds: [...selectedIds] };
      const result = await projectService.bulkDeleteProjects(payload);
      const count = result?.deleted_count ?? 0;

      // If the currently-open project was deleted, reset the canvas
      const hitCurrent = mode === 'all' || (currentProjectId && selectedIds.has(currentProjectId));
      if (hitCurrent && typeof newProject === 'function') {
        try { newProject(); } catch { /* ignore */ }
      }

      await loadSavedProjects();
      setSelectedIds(new Set());
      setBulkDeleteMode(null);
      toast.success(`${count} proyecto${count === 1 ? '' : 's'} eliminado${count === 1 ? '' : 's'}`, {
        duration: 2000, position: 'bottom-right',
      });
    } catch (err) {
      toast.error('Error al eliminar', { position: 'bottom-right' });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  return (
    <div className="pm-overlay" onClick={onClose} data-testid="projects-panel-overlay">
      {pendingLoadId && (
        <div className="pm-unsaved" onClick={(e) => e.stopPropagation()}
             data-testid="projects-unsaved-dialog">
          <p>Tenés cambios sin guardar en este proyecto.</p>
          <div className="pm-unsaved-btns">
            <button
              className="pm-unsaved-primary"
              onClick={async () => {
                const id = pendingLoadId; setPendingLoadId(null);
                try { await saveProject(); } catch (e) {
                  toast.error(`No se pudo guardar: ${e.message}`);
                  return;
                }
                await doLoad(id);
              }}
              data-testid="projects-unsaved-save"
            >
              Guardar y abrir
            </button>
            <button
              onClick={async () => {
                const id = pendingLoadId; setPendingLoadId(null);
                await doLoad(id);
              }}
              data-testid="projects-unsaved-discard"
            >
              Descartar cambios
            </button>
            <button onClick={() => setPendingLoadId(null)}
                    data-testid="projects-unsaved-cancel">
              Cancelar
            </button>
          </div>
        </div>
      )}
      <div className="pm-modal" onClick={e => e.stopPropagation()} data-testid="projects-panel">
        {/* Header */}
        <div className="pm-header">
          <div className="pm-header-left">
            <FolderOpen size={20} className="pm-header-icon" />
            <h2 className="pm-title">Proyectos</h2>
            <span className="pm-count">{savedProjects.length}</span>
          </div>
          <button className="pm-close" onClick={onClose} data-testid="projects-panel-close">
            <X size={20} />
          </button>
        </div>

        {/* Toolbar: search + new */}
        <div className="pm-toolbar">
          <div className="pm-search">
            <Search size={15} className="pm-search-icon" />
            <input
              className="pm-search-input"
              type="text"
              placeholder="Buscar proyecto..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              data-testid="projects-search-input"
            />
          </div>
          <button className="pm-new-btn" onClick={handleShowCreate} data-testid="projects-new-btn">
            <Plus size={16} />
            <span>Nuevo</span>
          </button>
        </div>

        {/* Create new project inline input */}
        {showCreateInput && (
          <div className="pm-create-row" data-testid="project-create-row">
            <FolderPlus size={16} className="pm-create-icon" />
            <input
              ref={createInputRef}
              className="pm-create-input"
              type="text"
              placeholder="Nombre del proyecto..."
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={handleCreateKeyDown}
              maxLength={80}
              data-testid="project-create-input"
            />
            <button
              className="pm-create-confirm"
              onClick={handleCreateConfirm}
              disabled={isCreating || !newName.trim()}
              data-testid="project-create-confirm"
            >
              {isCreating ? <Loader2 size={14} className="spin-icon" /> : <Check size={14} />}
            </button>
            <button className="pm-create-cancel" onClick={() => setShowCreateInput(false)}>
              <X size={14} />
            </button>
          </div>
        )}

        {/* Selection toolbar (always visible when there are projects) */}
        {!isLoadingProjects && savedProjects.length > 0 && (
          <div className="pm-select-bar" data-testid="projects-select-bar">
            <button
              className="pm-select-all-btn"
              onClick={toggleSelectAllVisible}
              data-testid="projects-select-all"
              title={allVisibleSelected ? 'Deseleccionar visibles' : 'Seleccionar todos los visibles'}
            >
              {allVisibleSelected
                ? <CheckSquare size={14} />
                : <Square size={14} />}
              <span>
                {selectedCount > 0
                  ? `${selectedCount} seleccionado${selectedCount === 1 ? '' : 's'}`
                  : 'Seleccionar'}
              </span>
            </button>

            <div className="pm-select-actions">
              {selectedCount > 0 && (
                <button
                  className="pm-bulk-btn pm-bulk-clear"
                  onClick={clearSelection}
                  data-testid="projects-clear-selection"
                >
                  Limpiar
                </button>
              )}
              <button
                className="pm-bulk-btn pm-bulk-danger"
                disabled={selectedCount === 0}
                onClick={() => setBulkDeleteMode('selected')}
                data-testid="projects-delete-selected"
                title="Eliminar seleccionados"
              >
                <Trash2 size={13} />
                <span>Eliminar ({selectedCount})</span>
              </button>
              <button
                className="pm-bulk-btn pm-bulk-danger-outline"
                onClick={() => setBulkDeleteMode('all')}
                data-testid="projects-delete-all"
                title="Eliminar todos los proyectos"
              >
                <Trash2 size={13} />
                <span>Borrar todos</span>
              </button>
            </div>
          </div>
        )}

        {/* Bulk-delete confirmation */}
        {bulkDeleteMode && (
          <div className="pm-bulk-confirm" data-testid="projects-bulk-confirm">
            <AlertTriangle size={16} className="pm-bulk-confirm-icon" />
            <div className="pm-bulk-confirm-text">
              {bulkDeleteMode === 'all'
                ? <>Eliminar <strong>TODOS</strong> tus proyectos ({savedProjects.length}). Esta acción no se puede deshacer.</>
                : <>Eliminar <strong>{selectedCount}</strong> proyecto{selectedCount === 1 ? '' : 's'} seleccionado{selectedCount === 1 ? '' : 's'}. Esta acción no se puede deshacer.</>
              }
            </div>
            <button
              className="pm-bulk-confirm-yes"
              disabled={isBulkDeleting}
              onClick={handleConfirmBulkDelete}
              data-testid="projects-bulk-confirm-yes"
            >
              {isBulkDeleting ? <Loader2 size={13} className="spin-icon" /> : 'Sí, eliminar'}
            </button>
            <button
              className="pm-bulk-confirm-no"
              disabled={isBulkDeleting}
              onClick={() => setBulkDeleteMode(null)}
              data-testid="projects-bulk-confirm-no"
            >
              Cancelar
            </button>
          </div>
        )}

        {/* Project list */}
        <div className="pm-body pm-body-compact" data-testid="projects-list">
          {isLoadingProjects && (
            <div className="pm-empty-state">
              <Loader2 size={24} className="spin-icon" />
              <span>Cargando...</span>
            </div>
          )}

          {!isLoadingProjects && filtered.length === 0 && (
            <div className="pm-empty-state">
              <FolderPlus size={36} className="pm-empty-icon" />
              <span className="pm-empty-title">
                {searchQuery ? 'Sin resultados' : 'No hay proyectos'}
              </span>
              <span className="pm-empty-sub">
                {searchQuery
                  ? 'Prueba con otro término de búsqueda'
                  : 'Crea tu primer proyecto para empezar'}
              </span>
              {!searchQuery && (
                <button className="pm-empty-action" onClick={handleShowCreate}>
                  <Plus size={14} /> Crear proyecto
                </button>
              )}
            </div>
          )}

          {filtered.map(project => {
            const isCurrent = project.id === currentProjectId;
            const isLoading = loadingId === project.id;
            const nodeCount = project.node_count ?? project.nodes?.length ?? 0;
            const execCount = project.execution_count || 0;
            const isBeingRenamed = renamingId === project.id;
            const isSelected = selectedIds.has(project.id);
            const isTemplate = !!project.is_template;

            return (
              <div
                key={project.id}
                className={`pm-row ${isCurrent ? 'pm-row-active' : ''} ${isSelected ? 'pm-row-selected' : ''}`}
                onClick={() => handleLoad(project.id)}
                data-testid={`project-row-${project.id}`}
              >
                {/* Checkbox */}
                <button
                  className="pm-row-check"
                  onClick={(e) => toggleSelect(project.id, e)}
                  data-testid={`project-check-${project.id}`}
                  title={isSelected ? 'Deseleccionar' : 'Seleccionar'}
                >
                  {isSelected
                    ? <CheckSquare size={15} />
                    : <Square size={15} />}
                </button>

                {/* Name + meta */}
                <div className="pm-row-main">
                  {isBeingRenamed ? (
                    <div className="pm-rename-row" onClick={e => e.stopPropagation()}>
                      <input
                        ref={renameInputRef}
                        className="pm-rename-input"
                        type="text"
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => handleRenameKeyDown(e, project.id)}
                        maxLength={80}
                        data-testid={`project-rename-input-${project.id}`}
                      />
                      <button
                        className="pm-rename-confirm"
                        onClick={() => handleRenameConfirm(project.id)}
                        disabled={isRenaming || !renameValue.trim()}
                      >
                        {isRenaming ? <Loader2 size={12} className="spin-icon" /> : <Check size={12} />}
                      </button>
                      <button className="pm-rename-cancel" onClick={(e) => { e.stopPropagation(); setRenamingId(null); }}>
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="pm-row-name" title={project.name}>
                        {isLoading && <Loader2 size={12} className="spin-icon pm-row-loading" />}
                        {project.name}
                      </span>
                      <span className="pm-row-sep">:</span>
                      <span className="pm-row-meta" data-testid={`project-nodecount-${project.id}`}>
                        <Layers size={11} />
                        {nodeCount} nodo{nodeCount === 1 ? '' : 's'}
                      </span>
                      {execCount > 0 && (
                        <span className="pm-row-meta pm-row-meta-faded">
                          <Zap size={11} />
                          {execCount}
                        </span>
                      )}
                      {isTemplate && (
                        <span className="pm-row-template-badge" data-testid={`project-template-badge-${project.id}`}>
                          <Star size={10} />
                          Plantilla
                        </span>
                      )}
                      {isCurrent && <span className="pm-row-badge">Activo</span>}
                    </>
                  )}
                </div>

                {/* Actions */}
                {!isBeingRenamed && (
                  <div className="pm-row-actions" onClick={e => e.stopPropagation()}>
                    <button
                      className={`pm-row-action ${isTemplate ? 'pm-row-action-active' : ''}`}
                      title={isTemplate ? 'Quitar de plantillas' : 'Guardar como plantilla'}
                      onClick={e => handleToggleTemplate(project, e)}
                      data-testid={`project-toggle-template-${project.id}`}
                    >
                      <Star size={12} fill={isTemplate ? 'currentColor' : 'none'} />
                    </button>
                    <button
                      className="pm-row-action"
                      title="Renombrar"
                      onClick={e => startRename(project, e)}
                      data-testid={`project-rename-${project.id}`}
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      className="pm-row-action"
                      title="Duplicar"
                      onClick={e => handleDuplicate(project.id, e)}
                      data-testid={`project-duplicate-${project.id}`}
                    >
                      <Copy size={12} />
                    </button>
                    <button
                      className="pm-row-action pm-danger"
                      title="Eliminar"
                      onClick={e => handleSingleDelete(project.id, e)}
                      data-testid={`project-delete-${project.id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

ProjectsPanel.displayName = 'ProjectsPanel';
export default ProjectsPanel;
